import { FILTER_FIELD_BY_KEY } from './filter-fields';
import { ensureConditionArray } from './filter.model';
import {
  FilterCondition,
  FilterField,
  FilterFieldKey,
  RangeBound,
} from './query.model';

/** The operator of each field that is not a range, as the filter form sends it. */
const OPERATORS: Partial<Record<FilterFieldKey, string>> = {
  name: 'contains',
  city: 'contains',
  investors: 'contains',
  reviewer: 'contains',
  keywords: 'contains',
  sectors: 'anyof',
  business_id: 'anyof',
  country_code: 'in',
  ownership_type: '=',
  review_status: '=',
};

const pad = (value: number) => String(value).padStart(2, '0');

/** A day as YYYY-MM-DD in local time. */
const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** A bound of a date or year range as the date the filter form sends. */
const boundToDate = (bound: RangeBound, end: boolean): Date | null => {
  if (typeof bound === 'number') {
    return end ? new Date(bound, 11, 31) : new Date(bound, 0, 1);
  }
  const match = /^(\d{4})-(\d\d)-(\d\d)$/.exec(bound ?? '');
  return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
};

/** A date of the API filter as a bound: a whole year as the year. */
const dateToBound = (
  field: FilterField,
  value: unknown,
  end: boolean
): RangeBound | undefined => {
  if (
    !(value instanceof Date) &&
    typeof value !== 'string' &&
    typeof value !== 'number'
  ) {
    return undefined;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  const yearEdge = end
    ? date.getMonth() === 11 && date.getDate() === 31
    : date.getMonth() === 0 && date.getDate() === 1;
  return field.kind === 'year-range' || yearEdge
    ? date.getFullYear()
    : toDay(date);
};

const isDateKind = (field: FilterField) =>
  field.kind === 'year-range' || field.kind === 'date-range';

/** One condition in the array format of the API, or null when it is empty. */
export const toApiCondition = (
  condition: FilterCondition
): unknown[] | null => {
  const { field: key, value } = condition;
  const field = FILTER_FIELD_BY_KEY.get(key);
  if (value.kind === 'raw') {
    return value.condition;
  }
  if (!field) {
    return null;
  }
  if (value.kind === 'range') {
    const date = isDateKind(field);
    const from = date ? boundToDate(value.from, false) : value.from;
    const to = date ? boundToDate(value.to, true) : value.to;
    if (from !== null && to !== null) {
      return [key, 'between', [from, to]];
    }
    if (from !== null) {
      return [key, '>=', from];
    }
    return to !== null ? [key, '<=', to] : null;
  }
  const operator = OPERATORS[key] ?? '=';
  if (value.kind === 'text') {
    return value.value ? [key, operator, value.value] : null;
  }
  if (!value.values.length) {
    return null;
  }
  if (key === 'keywords') {
    return [key, operator, value.values.join('&')];
  }
  return [
    key,
    operator,
    value.kind === 'enum' ? value.values[0] : value.values,
  ];
};

/**
 * The conditions in the filter format of the API, joined with "and" as the
 * filter form joins them: one condition alone, none as an empty array.
 */
export const toApiFilter = (conditions: FilterCondition[]): unknown[] => {
  const parts = conditions
    .map(toApiCondition)
    .filter((x): x is unknown[] => x !== null);
  return parts.length === 1
    ? parts[0]
    : parts.reduce(
        (acc: unknown[], cur, idx) => [
          ...acc,
          ...(idx > 0 ? ['and', cur] : [cur]),
        ],
        [] as unknown[]
      );
};

const rawCondition = (condition: unknown[]): FilterCondition => ({
  field: condition[0] as FilterFieldKey,
  value: { kind: 'raw', condition },
});

const fromApiCondition = (condition: unknown[]): FilterCondition => {
  const [property, operator, value] = condition as [string, string, unknown];
  const field = FILTER_FIELD_BY_KEY.get(property as FilterFieldKey);
  if (!field) {
    return rawCondition(condition);
  }
  const key = field.key;
  if (field.kind.endsWith('-range')) {
    const bound = (x: unknown, end: boolean): RangeBound | undefined =>
      isDateKind(field)
        ? dateToBound(field, x, end)
        : typeof x === 'number' ||
            (typeof x === 'string' && x !== '' && !isNaN(+x))
          ? +x
          : undefined;
    let from: RangeBound | undefined = null;
    let to: RangeBound | undefined = null;
    if (operator === 'between' && Array.isArray(value) && value.length === 2) {
      from = bound(value[0], false);
      to = bound(value[1], true);
    } else if (operator === '>=') {
      from = bound(value, false);
    } else if (operator === '<=') {
      to = bound(value, true);
    } else {
      return rawCondition(condition);
    }
    return from === undefined || to === undefined
      ? rawCondition(condition)
      : { field: key, value: { kind: 'range', from, to } };
  }
  if (operator !== OPERATORS[key]) {
    return rawCondition(condition);
  }
  if (field.kind === 'text') {
    return typeof value === 'string'
      ? { field: key, value: { kind: 'text', value } }
      : rawCondition(condition);
  }
  if (key === 'keywords') {
    return typeof value === 'string'
      ? { field: key, value: { kind: 'multi', values: value.split('&') } }
      : rawCondition(condition);
  }
  if (field.kind === 'enum') {
    return typeof value === 'string'
      ? { field: key, value: { kind: 'enum', values: [value] } }
      : rawCondition(condition);
  }
  const values = (Array.isArray(value) ? value : [value]).filter(
    (x): x is string | number => typeof x === 'string' || typeof x === 'number'
  );
  return { field: key, value: { kind: 'multi', values } };
};

/** The conditions of a filter in the format of the API; "and" is assumed. */
export const fromApiFilter = (
  filter: unknown[] | null | undefined
): FilterCondition[] =>
  (filter && filter.length ? ensureConditionArray(filter) : []).map(
    fromApiCondition
  );

/** A key for comparing conditions; dates count by their time. */
export const conditionKey = (condition: FilterCondition) =>
  JSON.stringify(toApiCondition(condition));

/** Whether two filters have the same conditions, in any order. */
export const sameFilter = (
  a: unknown[] | null | undefined,
  b: unknown[] | null | undefined
) => {
  const keys = (filter: unknown[] | null | undefined) =>
    fromApiFilter(filter).map(conditionKey).sort().join('\n');
  return keys(a) === keys(b);
};
